/** @module csi/heading/RefImage */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/query",
	"dojo/topic",
	"csi/_base/underscore",
	"csi/action",
	"csi/action/RefImageDropController",
	"csi/action/ImageReplace",
	"csi/action/ImageWhereUsed",
	"csi/core/ExtendedPath",
	"csi/core/Key",
	"csi/def/Heading",
	"csi/heading/Aggregate",
	"csi/heading/Attribute",
	"csi/heading/AttributeAction",
	"csi/heading/FileSizeRenderer",
	"csi/heading/RefImageBrowse",
	"csi/heading/RefImageRemove",
	"csi/heading/RefImageView",
	"csi/heading/RefreshThumbnail",
	"csi/html",
	"csi/html/Rule",
	"csi/image/_base",
	"csi/locale",
	"csi/model/image",
	"csi/reflection",
	"csi/service/imageIntentService",
	"csi/state/rootStore",
	"csi/widget/_DndHTML5Mixin",
	"csi/widget/ImageContainer",
	"csi/xml",
	"dojo/text!csi/templates/imageTooltip.html",
], function(array, declare, lang, domClass, query, topic, _, csiAction, RefImageDropController, ImageReplace, ImageWhereUsed, ExtendedPath, Key, Heading,
	AggregateInline, Attribute, AttributeAction, FileSizeRenderer, RefImageBrowse, RefImageRemove, RefImageView,
	RefreshThumbnail, html, Rule, csiImage, locale, csiImageModel, reflection, {imageResolveImageIntent}, {browserStore}, _DndHTML5Mixin,
	ImageContainer, xml, tooltipTemplate) {

	let _viewAction;

	html.registerDataAttribute("DATA_IMG_KEY", "imgkey");
	html.registerDataAttribute("DATA_IMG_SIZE", "imgsize");
	html.registerDataAttribute("DATA_IMG_URL", "imgurl");
	html.registerDataAttribute("DATA_IMG_CONTAINER_URL", "imgcurl");

	return declare([Attribute, _DndHTML5Mixin], {
		// {String:Heading}: headings used to render tooltip mapped by key in template
		_tooltipDefs: null,

		/** @property {boolean} bDisableBrowse - to determine if browse action is available on tooltip */
		bDisableBrowse: false,

		/** @property {boolean} bDisableRemove - to determine if remove action is available on tooltip */
		bDisableRemove: false,

		/** @property {boolean} bOmitFromExcel - to determine if image should be exported to Excel */
		bOmitFromExcel: false,

		constructor: function(/*String*/type, /*String?*/format) {
			// summary: initialize headings for tooltip
			var rcDoc = reflection.getClass("DocumentRevision");
			var rcImage = reflection.getClass("Image");

			var newHeading = function(id, path, rc, classRenderer, name) {
				var h = lang.mixin(new Heading(), {
					m_id: id,
					m_name: name,
					m_path: path,
					classRenderer: classRenderer
				});

				h.resolveHeading(locale.localeResolver, null, rc);
				return h;
			};

			var newRule = function(exp, path) {
				return lang.mixin(new Rule(), {
					m_class: html.CSS_HIDE,
					m_exp: exp,
					m_path: path
				});
			};

			this._tooltipDefs = {
				image: newHeading("Thumbnail", null, rcImage, "csi/heading/UrlImage"),	// specify renderer class for testing when reflection not loaded and Image format is unknown
				name: newHeading(xml.ATTR_NAME, null, rcImage),
				date: newHeading("ModifiedAt", null, rcImage, null, "DateAdded"),
				size: newHeading("FileSize", null, rcImage, FileSizeRenderer, "Size"),
				tags: newHeading("Tags", null, rcImage),
				doc: newHeading("File", "Child:GeneratedFor", rcDoc),
				files: newHeading("AlternateFiles", "Child:GeneratedFor", rcDoc),
				imageRule: newRule(false),		// changed dynamically based on image size setting
				tagsRule: newRule("!validValue('Tags')"),
				docRule: newRule("!validValue('GeneratedFor')"),
				intentRule: newRule("!validValue('ImageIntent')"),
				filesRule: newRule("!validValue('AlternateFiles')", "Child:GeneratedFor")
			};
		},

		canBeLarge: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return 2 for large images. 0 otherwise
			switch (ctx.prefImageSize) {
				case reflection.IMAGE_SIZE_ICON:
				case reflection.IMAGE_SIZE_SMALLEST:
				case reflection.IMAGE_SIZE_SMALL:
					return 0;
				default:
					return 2;
			}
		},

		canLink: function() {
			// summary: images are already anchors
			return false;
		},

		/**
		 * Store data attributes that will be used to open Lightbox with rendered images
		 * @param {Context} ctx 
		 * @param {Heading} h 
		 * @param {HTMLElement} domNode 
		 */
		setImageActionAttributes(ctx, h, domNode) {
			const imageNode = this._getImageNode(ctx, h);
			if (imageNode) {
				html.setDataAttribute(domNode, html.DATA_IMG_URL, imageNode.$URL);
				html.setDataAttribute(domNode, html.DATA_IMG_CONTAINER_URL, ctx.getNodeURL());

				if (ctx.hasContainerKey()) {
					html.setDataAttribute(domNode, html.DATA_IMG_KEY, ctx.keyValue);
				}
			}
		},

		getQuerySpec: function(/*Context*/ctx, /*Heading*/h, /*Spec*/spec) {
			// summary: do not allow filtering by referenced images
			return null;
		},

		/**
		 * @override
		 * Override parameters for tooltip
		 */
		getTooltipParams: function(ctx, h, refNode) {
			const params = this.inherited(arguments);

			// connect node for image cell is the image itself, or icon representing the image;
			// if neither exists, then use default
			params.connectNode = query(".csiActionIcon", refNode)[0] || query("img", refNode)[0] || refNode;

			// if icon representing image, then delay should be as short as possible; otherwise use default
			params.delay = query(".iconPhoto", refNode)[0] ? 1 : 0;

			return params;
		},

		isTabStop: function(/*Context*/ctx, /*Heading*/h) {
			// summary: edits with dialog so skip when tabbing
			return false;
		},

		toHtmlStatic: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/refNode, /*ActionData*/actionData) {
			// summary: render image and add onclick action if image exists - it is possible that imageSize
			// to render image is different than view image size, so be careful to track per image
			var imageSizeOld = ctx.prefImageSize;
			var imageSize = html.getDataAttribute(refNode, html.DATA_IMG_SIZE);
			if (imageSize) {
				ctx.prefImageSize = imageSize;
			}

			var className = this._getImageCSS(ctx);
			var src = this._getImageSrc(ctx, h, this._getImageId(ctx));
			var domNode = refNode;
			const {restrictLightbox} = browserStore;
			const imageNode = this._getImageNode(ctx, h);
			const bTurntable = imageNode && csiImageModel.isTurntableImage(imageNode);
			var bIcon = false;
			var bImage = false;

			if (ctx.bExcel) {
				// nothing more to do if image does not exist, is an icon, or explicitly omitted from excel
				if (this.bOmitFromExcel || !src || (ctx.prefImageSize === reflection.IMAGE_SIZE_ICON)) {
					return false;
				}

				// render a special tag with the src property, and the max dimensions of the image (but only if the context image size is different than the view's image size)
				var params = {src};

				if (ctx.prefImageSize != ctx.view.imageSize) {
					const {height, width} = csiImageModel.getSizeByImageSize(ctx.prefImageSize);
					params.maxheight = height;
					params.maxwidth = width;
				}

				html.create("csi_img", params, refNode);
				return true;
			} else if (ctx.bCaptureHtml) {
				// don't use csiImage.create as it waits for image to render; instead, just create image tag immediately
				if (!src || (ctx.prefImageSize === reflection.IMAGE_SIZE_ICON)) {
					return false;
				}

				html.create("img", {className, src}, refNode);
				return true;
			}

			if (!src && actionData?.action.isIcon()) {
				// render icon to browse for image if heading has explicit action
				var span = html.create("span", {"class": "csi-drop-msg"}, refNode);
				domNode = actionData.action.toHtmlControl(ctx, span, false, bTurntable);
				actionData.setClickNode(domNode, true);
				bIcon = true;
			} else if (src && (ctx.prefImageSize === reflection.IMAGE_SIZE_ICON)) {
				// if preferences indicate to use icon and there is a source, render now and image will be loaded in tooltip; clicking on icon will load full image
				var iconAction = this._getViewAction(ctx);

				domNode = iconAction.toHtmlControl(ctx, refNode, false, bTurntable);
				this.setImageActionAttributes(ctx, h, domNode);
				bIcon = true;

				if (ctx.allowActions() && !ctx.bReserveClick && !restrictLightbox) {
					ctx.applyActionContext(domNode, iconAction.m_id);
				}
			} else if (src) {
				// copy context before loading image as the context can/will change by the time the image is loaded
				var ctxCopy = ctx.copyContext();
				var predictedSize = this._getPredictedSize(ctx, h);

				bImage = true;

				(bTurntable ?
					this.createTurntableImage(ctx, refNode, imageNode, className, src, predictedSize) :
					csiImage.create(refNode, src, null, className, predictedSize[0], predictedSize[1])
				).then((img) => {
					// continue processing if image loaded successfully
					if (!csiImage.isError(img) && !restrictLightbox) {
						this._onImageLoad(ctxCopy, h, img);

						// store attributes for lightgallery
						this.setImageActionAttributes(ctxCopy, h, img);
					}

					// notify of DOM change when image loaded (whether successfully or with error image)
					if (img) {
						topic.publish("/csi/refimage/load", ctx, h, img);
						topic.publish("/csi/view/change", img);
					}
				});
			}

			// actionData will be valid if user has edit access on attribute even though renderer action is to view image;
			// editable actions will be in tooltip
			if (actionData && (src || !actionData.action.isIcon())) {
				actionData.bTooltip = true;
			}

			// store image size on node in case it is edited and needs to be regenerated
			html.setDataAttribute(domNode, html.DATA_IMG_SIZE, ctx.prefImageSize);

			// notify listeners of image/icon being drawn
			topic.publish("/csi/refimage/draw", ctx, h, refNode, src, bImage);

			// restore previous image size
			ctx.prefImageSize = imageSizeOld;

			// initialize Drag and Drop
			if (actionData) {
				this.dndHTML5Init(refNode, lang.hitch(this, this._onDropImage, ctx.view, h, ctx.keyValue), null, false, null, ctx.view.bPopup);
			}

			return bIcon || !!src;
		},

		/**
		 * use ImageContainer component to render turntalbe
		 * @param {Context} ctx 
		 * @param {DOMNode} refNode - dom node to insert image to
		 * @param {C8Node} imageNode 
		 * @param {string} className - image css class name
		 * @param {string} src - url of thumbnail image to use instead of gallery
		 * @returns {Deferred}}
		 */
		createTurntableImage: function(ctx, refNode, imageNode, className, src, predictedSize) {
			const imageId = this._getImageId(ctx);
			const imageList = csiImageModel.getImageGallerySrc(imageNode, imageId);
			const params = {
				imageClass: className,
				imageList: imageList || src,
				disableTurntable: !imageList,
				isTurntable: true,
				predictedHeight: predictedSize[0],
				predictedWidth: predictedSize[1],
			}
			const imageContainer = new ImageContainer(params).placeAt(refNode);
			return imageContainer.deferred;
		},

		/*
		 * Call back for drag drop onDrop event
		 * @param {csi/view/Widget} view
		 * @param {Heading} heading
		 * @param {string} keyValue - image number or label or "" for default image
		 * @param {File[]} files - list of dropped files
		 * @param {DomNode} refNode
		 */
		_onDropImage: function(view, heading, keyValue, files, refNode) {
			var dropAction = lang.mixin(new AttributeAction(), {
				m_id: "ImageDrop",
				bKeepViewEnabled: true,
				classController: RefImageDropController
			});

			csiAction.invoke(view, refNode, dropAction, {
				_files: files,
				heading: heading,
				keyContainer: keyValue,
				refNode: refNode,
				ren: this
			});
		},

		toTooltip: async function(/*Context*/ctx, /*Heading*/h, /*Tooltip*/tooltip) {
			// summary: generate tooltip for image
			var actionData = this._getRenderAction(ctx, h);
			var image = this._getImageNode(ctx, h);

			// if browsing image is not in tooltip and image does not exist, then no other tooltip actions make sense
			if (!image && (!actionData || actionData.action.isIcon())) {
				return;
			}

			// add image editing actions
			var bBrowse = actionData && (image || !actionData.action.isIcon());

			array.forEach(this._getTooltipActions(ctx, h, bBrowse), function(a) {
				tooltip.addAction(a);
			});

			// add expression action if appropriate
			tooltip.addAction(this._getExpressionAction(ctx, h, actionData));

			// add content to tooltip
			if (image) {
				// pre-cache document from which image is generated since it is needed for tooltip content
				var bIcon = (ctx.prefImageSize == reflection.IMAGE_SIZE_ICON);
				var ctxCopy = ctx.view.getContext(image.$URL, ctx.getNodeURL());
				ctxCopy.bReserveClick = true;
				ctxCopy.prefImageSize = reflection.IMAGE_SIZE_SMALL;
				ctxCopy.setReadOnly(true);

				// exclude image if image displayed in view (only show if icon)
				this._tooltipDefs.imageRule.m_exp = !bIcon;

				// resolve nodes necessary for tooltip (will include GeneratedFor)
				await imageResolveImageIntent(image, ctxCopy.getParentURL(), ctxCopy.cache);

				const domNode = html.templateToHtml(ctxCopy, tooltipTemplate, this._tooltipDefs);

				if (bIcon) {
					tooltip.fadeDelay = 1;
				} else if (!domNode.innerText) {
					return;
				}

				tooltip.setContent(domNode);
			}
		},

		toValue: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return name of image
			return xml.nodeGetName(this._getImageNode(ctx, h));
		},

		toThumbnail: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return href for thumbnail image
			return this._getImageSrc(ctx, h, reflection.ATTR_THUMBNAIL);
		},

		toViewable: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return href for viewable image
			return this._getImageSrc(ctx, h, reflection.ATTR_VIEWABLE);
		},

		_getAction: function() {
			// summary: return action to browse for image
			var a = new RefImageBrowse();
			a.setMaterialIcon("add_a_photo");
			return a;
		},

		_getCSS: function(/*String*/type, /*String?*/format) {
			// summary: consolidate image CSS
			return "attrImage";
		},

		_getImageCSS: function(/*Context*/ctx) {
			// summary: return CSS class to use on img tag
			return csiImage.getImageSizeCss(ctx.prefImageSize);
		},

		_getImageId: function(/*Context*/ctx) {
			// summary: return image attribute to use based on context
			return csiImage.getImageIdByContext(ctx);
		},

		_getImageNode: function(/*Context*/ctx, /*Heading*/h) {
			// summary: return referenced image node
			return ctx.cache.getNode(this._getValue(ctx, h), true);
		},

		_getImageSrc: function(/*Context*/ctx, /*Heading*/h, /*String*/idImage) {
			// summary: return href for image
			return csiImage.getImageSrc(this._getImageNode(ctx, h), null, idImage);
		},

		/**
		 * If the available width is specified, then calculate the height the image will turn out to be when rendered
		 * @param {Context} ctx
		 * @return {[height: number, width: number]}
		 */
		_getPredictedSize(ctx, h) {
			const {availableHeight, availableWidth} = ctx;
			if (!availableWidth) {
				return [0, 0];
			}

			const node = this._getImageNode(ctx, h);
			if (!node) {
				return [0, 0];
			}

			const targetHeight = availableHeight || csiImage.getImageSizeMaxHeight(ctx.prefImageSize);
			const height = csiImage.getScaledImageHeight(node, targetHeight, availableWidth);
			const width = Math.ceil(height * node.Width / node.Height);

			return [height, width];
		},

		_getRenderCSSAsSub: function(/*Context*/ctx, /*Heading*/h) {
			// summary: if rendering image as sub-heading, still need to apply regular CSS
			return "attrImage";
		},

		_getTagAction: function(/*Context*/ctx, /*Heading*/h, /*String?*/path) {
			// summary: return action for tagging image
			const containerNode = ctx.getNode();
			let a;

			if (!xml.nodeHasAccess(containerNode, xml.RIGHT_WRITE, h.m_id)) {
				return null;
			}

			if (ctx.push(path)) {
				const key = new Key({
					extendedPath: ExtendedPath.create(reflection.TYPE_IMAGE),
					id: reflection.ATTR_TAGS,
				});
				const tagsHeading = ctx.view.getHeading(key);

				a = lang.mixin(new AggregateInline(), {
					classController: "csi/heading/TagController",
					heading: tagsHeading,
					m_id: "Tags",
					m_path: path,
					m_rcTarget: key.extendedPath.startClass,

					// to prevent refreshing of image container while editing tags of images
					m_postAction: () => true
				});
				a.resolve();

				ctx.pop();
			}

			return a;
		},

		_getTooltipActions: function(/*Context*/ctx, /*Heading*/h, /*Boolean*/bBrowse) {
			// summary: return actions to browse and reset file
			const a = [];
			const imageUrl = this._getValue(ctx, h);
			const imageNode = ctx.cache.get(imageUrl);
			const imageData = csiImageModel.getImageData(imageNode, ctx.getNode(), h.m_id, null, null, ctx.keyValue);
			let path = xml.path(h.m_id);

			if (h.hasPathIndex()) {
				path = xml.path(path, xml.PATH_OP_INDEX, h.m_index);
			}

			if (ctx.bReserveClick && !restrictLightbox) {
				// if selecting images, then use tooltip action to trigger lightbox
				a.push(this._mixinTooltipAction(new RefImageView(), h, true));
			}

			if (bBrowse && !this.bDisableBrowse) {
				a.push(this._mixinTooltipAction(new RefImageBrowse(), h));
			}

			a.push(this._mixinTooltipAction(new RefreshThumbnail(), h));

			if (!this.bDisableRemove) {
				a.push(this._mixinTooltipAction(new RefImageRemove(), h));
			}

			const bCareLabels = h.m_id === "LanguageIcons";

			// the Replace Image option should not be displayed for Care Icons
			if (imageData.showWhereUsed && !bCareLabels) {
				a.push(this._mixinTooltipAction(new ImageReplace().init(imageUrl, ctx.getNodeURL(), h), h));
			}

			a.push(this._getTagAction(ctx, h, path));

			// for ref image we don't define "showWhereUsed" (image.ts=>getImageData())
			// but there are one exception: "LanguageIcon" for Care & Composition - Care Labels (C8-68771)
			if (imageData.showWhereUsed || bCareLabels) {
				a.push(this._mixinTooltipAction(new ImageWhereUsed().init(imageUrl), h));
			}

			return a;
		},

		_getViewAction: function(/*Context*/ctx) {
			// summary: ensure action to view image exists on view
			if (!_viewAction) {
				_viewAction = new RefImageView();
				_viewAction.m_class = "iconPhoto";
				_viewAction.setMaterialIcon("photo");
			}

			// ensure that view can access action
			ctx.defView._addViewAction(_viewAction);

			return _viewAction;
		},

		_onImageLoad: function(/*Context*/ctx, /*Heading*/h, /*DOMNode*/img) {
			// summary: extension point for processing image once loaded
			if (ctx.allowActions() && !ctx.bReserveClick && !ctx.bTooltip) {
				ctx.applyActionContext(img, this._getViewAction(ctx).m_id);

				// add action CSS class to get pointer cursor
				domClass.add(img, html.CSS_ACTION);
			}
		},

		_toFilterValue: function(/*Context*/ctx, /*Heading*/h, /*Any*/value) {
			// summary: sort by name of node containing image rather than image node's name
			return locale.getNodePropertyName(ctx.getNode(), ctx.cache);
		},

		/**
		 * Preserve drop zone css class if any
		 * @param {Context} ctx
		 * @param {Heading} h
		 * @param {DOMNode} refNode
		 */
		_getRenderCSS: function(ctx, h, refNode) {
			var dropZoneCssClass = "";
			var dndHTML5CssClass = ctx.view.bPopup ? "csi-dialog-drop-zone" : "csi-drop-zone"
			if (dndHTML5CssClass && domClass.contains(refNode, dndHTML5CssClass)) {
				dropZoneCssClass = " " + dndHTML5CssClass;
			}
			return this.inherited(arguments) + dropZoneCssClass;
		}

	});
});